/**
 * FileUpload — Drag-and-drop file upload component with preview.
 *
 * Features:
 * - Drag-and-drop zone with visual feedback
 * - Click to browse file picker
 * - Multiple file support
 * - File type filtering (accept prop)
 * - Max file size validation
 * - File list with remove button
 * - Image thumbnail preview
 * - Upload progress indicator
 * - Accessible (keyboard + screen reader)
 *
 * Usage:
 * ```tsx
 * <FileUpload
 *   value={files}
 *   onChange={setFiles}
 *   accept="image/*,.pdf"
 *   maxSizeMB={10}
 *   multiple
 * />
 * ```
 */

import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Upload,
  X,
  FileText,
  ImageIcon,
  File as FileIcon,
  AlertCircle,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────────

export interface UploadedFile {
  /** Unique ID for this file entry */
  id: string;
  /** Original file name */
  name: string;
  /** MIME type */
  type: string;
  /** File size in bytes */
  size: number;
  /** URL for preview (object URL or remote URL) */
  previewUrl?: string;
  /** Upload progress 0-100 */
  progress?: number;
  /** Error message if upload failed */
  error?: string;
  /** The raw File object (only available before upload) */
  file?: File;
}

export interface FileUploadProps {
  /** Current files */
  value: UploadedFile[];
  /** Called when files change (add/remove) */
  onChange: (files: UploadedFile[]) => void;
  /** Called when new files are selected (for upload handling) */
  onUpload?: (files: File[]) => void;
  /** Accepted file types (same format as input accept attribute) */
  accept?: string;
  /** Allow multiple files */
  multiple?: boolean;
  /** Maximum file size in MB */
  maxSizeMB?: number;
  /** Maximum number of files */
  maxFiles?: number;
  /** Custom label */
  label?: string;
  /** Helper text below the dropzone */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Disabled state */
  disabled?: boolean;
  /** Read-only state (show files but no add/remove) */
  readOnly?: boolean;
  /** Custom class name */
  className?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────

function generateId(): string {
  return `file_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function isImageType(type: string): boolean {
  return type.startsWith('image/');
}

function getFileIcon(type: string) {
  if (isImageType(type)) return ImageIcon;
  if (type === 'application/pdf') return FileText;
  return FileIcon;
}

// ─── Component ──────────────────────────────────────────────────────

export function FileUpload({
  value,
  onChange,
  onUpload,
  accept,
  multiple = false,
  maxSizeMB = 10,
  maxFiles = 10,
  label = 'Upload files',
  helperText,
  error,
  disabled = false,
  readOnly = false,
  className,
}: FileUploadProps) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  const maxSizeBytes = maxSizeMB * 1024 * 1024;

  // ─── Validate + Add Files ─────────────────────────────────────

  const processFiles = React.useCallback(
    (fileList: FileList | File[]) => {
      setValidationError(null);
      const files = Array.from(fileList);

      // Check max files
      if (!multiple && files.length > 1) {
        setValidationError('Only one file allowed');
        return;
      }
      const totalFiles = value.length + files.length;
      if (totalFiles > maxFiles) {
        setValidationError(`Maximum ${maxFiles} files allowed`);
        return;
      }

      const validFiles: UploadedFile[] = [];
      const errors: string[] = [];

      for (const file of files) {
        // Size check
        if (file.size > maxSizeBytes) {
          errors.push(`${file.name} exceeds ${maxSizeMB}MB limit`);
          continue;
        }

        const uploadedFile: UploadedFile = {
          id: generateId(),
          name: file.name,
          type: file.type,
          size: file.size,
          previewUrl: isImageType(file.type)
            ? URL.createObjectURL(file)
            : undefined,
          progress: 0,
          file,
        };
        validFiles.push(uploadedFile);
      }

      if (errors.length > 0) {
        setValidationError(errors.join('. '));
      }

      if (validFiles.length > 0) {
        const newValue = multiple ? [...value, ...validFiles] : validFiles;
        onChange(newValue);
        onUpload?.(validFiles.map((f) => f.file!));
      }
    },
    [value, onChange, onUpload, multiple, maxFiles, maxSizeBytes, maxSizeMB],
  );

  // ─── Remove File ──────────────────────────────────────────────

  const removeFile = React.useCallback(
    (id: string) => {
      const file = value.find((f) => f.id === id);
      if (file?.previewUrl && file.file) {
        URL.revokeObjectURL(file.previewUrl);
      }
      onChange(value.filter((f) => f.id !== id));
    },
    [value, onChange],
  );

  // ─── Drag & Drop Handlers ────────────────────────────────────

  const handleDragEnter = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !readOnly) setIsDragging(true);
    },
    [disabled, readOnly],
  );

  const handleDragLeave = React.useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!disabled && !readOnly) setIsDragging(true);
    },
    [disabled, readOnly],
  );

  const handleDrop = React.useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (disabled || readOnly) return;
      if (e.dataTransfer.files.length > 0) {
        processFiles(e.dataTransfer.files);
      }
    },
    [disabled, readOnly, processFiles],
  );

  // ─── Click Handler ────────────────────────────────────────────

  const handleClick = React.useCallback(() => {
    if (!disabled && !readOnly) {
      inputRef.current?.click();
    }
  }, [disabled, readOnly]);

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        processFiles(e.target.files);
      }
      // Reset input so same file can be selected again
      e.target.value = '';
    },
    [processFiles],
  );

  // ─── Cleanup object URLs on unmount ───────────────────────────

  React.useEffect(() => {
    return () => {
      for (const file of value) {
        if (file.previewUrl && file.file) {
          URL.revokeObjectURL(file.previewUrl);
        }
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Display error ────────────────────────────────────────────

  const displayError = error || validationError;

  // ─── Render ───────────────────────────────────────────────────

  return (
    <div className={cn('space-y-3', className)}>
      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        onChange={handleInputChange}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />

      {/* Dropzone */}
      {!readOnly && (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={label}
          aria-disabled={disabled}
          onClick={handleClick}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleClick();
            }
          }}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={cn(
            'relative flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed px-6 py-8 transition-colors cursor-pointer',
            'border-[var(--color-border)] bg-[var(--color-muted)]/30',
            'hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-muted)]/50',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2',
            isDragging && 'border-[var(--color-primary)] bg-[var(--color-primary)]/5',
            disabled && 'pointer-events-none opacity-50',
            displayError && 'border-[var(--color-destructive)]',
          )}
        >
          <Upload
            className={cn(
              'h-8 w-8',
              isDragging
                ? 'text-[var(--color-primary)]'
                : 'text-[var(--color-muted-foreground)]',
            )}
          />
          <div className="text-center">
            <p className="text-sm font-medium text-[var(--color-foreground)]">
              {isDragging ? 'Drop files here' : label}
            </p>
            <p className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {helperText ??
                `Drag & drop or click to browse${maxSizeMB ? ` (max ${maxSizeMB}MB)` : ''}`}
            </p>
          </div>
        </div>
      )}

      {/* Error */}
      {displayError && (
        <div className="flex items-center gap-1.5 text-xs text-[var(--color-destructive)]">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          <span>{displayError}</span>
        </div>
      )}

      {/* File List */}
      {value.length > 0 && (
        <ul className="space-y-2" role="list" aria-label="Uploaded files">
          {value.map((file) => {
            const Icon = getFileIcon(file.type);
            return (
              <li
                key={file.id}
                className={cn(
                  'flex items-center gap-3 rounded-lg border border-[var(--color-border)] bg-[var(--color-card)] p-3',
                  file.error && 'border-[var(--color-destructive)]/50',
                )}
              >
                {/* Thumbnail or Icon */}
                {file.previewUrl ? (
                  <img
                    src={file.previewUrl}
                    alt={file.name}
                    className="h-10 w-10 rounded object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded bg-[var(--color-muted)]">
                    <Icon className="h-5 w-5 text-[var(--color-muted-foreground)]" />
                  </div>
                )}

                {/* File Info */}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[var(--color-foreground)]">
                    {file.name}
                  </p>
                  <p className="text-xs text-[var(--color-muted-foreground)]">
                    {formatFileSize(file.size)}
                    {file.error && (
                      <span className="ml-2 text-[var(--color-destructive)]">
                        {file.error}
                      </span>
                    )}
                  </p>
                  {/* Progress */}
                  {file.progress !== undefined &&
                    file.progress > 0 &&
                    file.progress < 100 && (
                      <Progress value={file.progress} className="mt-1.5 h-1.5" />
                    )}
                </div>

                {/* Remove Button */}
                {!readOnly && !disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(file.id);
                    }}
                    aria-label={`Remove ${file.name}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
